import {
  ChangeDetectorRef, Component, OnInit,
} from '@angular/core';
import {
  ActivatedRoute, Router,
} from '@angular/router';
import { NotificationService } from '../../../application/services/NotificationService';
import { HttpResponse } from '../../../application/types/HttpResponse';
import { EventApiService } from '../../services/event-api/EventApiService';
import { ChampionshipApiService } from '../../services/championship-api/ChampionshipApiService';
import { TeamApiService } from '../../services/team-api/TeamApiService';
import { SeasonApiService } from '../../services/season-api/SeasonApiService';
import { UserManagementApiService } from '../../../users/services/user-management-api/UserManagementApiService';
import { DashboardUser } from '../../../users/types/User';
import {
  SaveEventData,
  Championship,
  Season,
  SeasonPhase,
  SaveEventParticipantData,
  SportsEvent,
  Team,
} from '../../types/Event';

@Component({
  selector: 'app-event-form-page',
  standalone: false,
  templateUrl: './event-form-page.component.html',
  styleUrl: './event-form-page.component.sass',
})
export class EventFormPageComponent implements OnInit {
  public mode: 'create' | 'edit' = 'create';
  public eventId: string | null = null;
  public teams: Team[] = [];
  public championships: Championship[] = [];
  public seasons: Season[] = [];
  public users: DashboardUser[] = [];
  public currentUser: DashboardUser | null = null;
  public formData: SaveEventData = this.createEmptyForm();
  public seasonPhaseMode: 'regular' | 'playoff' = 'regular';
  public selectedParticipantUserId = '';
  public loading = false;
  public saving = false;
  public errorMessage: string | null = null;

  public constructor(
    private route: ActivatedRoute,
    private router: Router,
    private eventApi: EventApiService,
    private teamApi: TeamApiService,
    private championshipApi: ChampionshipApiService,
    private seasonApi: SeasonApiService,
    private userApi: UserManagementApiService,
    private notificationService: NotificationService,
    private changeDetector: ChangeDetectorRef,
  ) { }

  public async ngOnInit(): Promise<void> {
    this.eventId = this.route.snapshot.paramMap.get('eventId');
    this.mode = this.eventId ? 'edit' : 'create';
    this.loading = true;

    try {
      await this.loadDictionaries();
      if (this.eventId) {
        await this.loadEvent(this.eventId);
      }
    } catch (error) {
      this.handleUnknownError(error);
    } finally {
      this.loading = false;
      this.changeDetector.detectChanges();
    }
  }

  public get title(): string {
    return this.mode === 'edit' ? 'Редактирование события' : 'Новое событие';
  }

  public async saveEvent(): Promise<void> {
    this.saving = true;
    this.errorMessage = null;

    try {
      const response = this.eventId
        ? await this.eventApi.updateEvent(this.eventId, this.formData)
        : await this.eventApi.createEvent(this.formData);
      const result = this.unwrapResponse(response);
      if (!result) {
        return;
      }

      this.notificationService.notify(
        'event',
        this.mode === 'edit' ? 'Событие обновлено' : 'Событие добавлено',
        `${result.homeTeam.name} - ${result.awayTeam.name}`,
      );
      await this.router.navigate([
        '/dashboard/events',
        result.slug || result.id,
      ]);
    } catch (error) {
      this.handleUnknownError(error);
    } finally {
      this.saving = false;
      this.changeDetector.detectChanges();
    }
  }

  public trackById(_index: number, item: { id: string }): string {
    return item.id;
  }

  public get availableTeams(): Team[] {
    if (!this.formData.championshipId) {
      return [];
    }

    return this.teams.filter((team) => team.championshipId === this.formData.championshipId);
  }

  public get availableHomeTeams(): Team[] {
    return this.availableTeams.filter((team) => team.id !== this.formData.awayTeamId);
  }

  public get availableAwayTeams(): Team[] {
    return this.availableTeams.filter((team) => team.id !== this.formData.homeTeamId);
  }

  public get availableSeasons(): Season[] {
    if (!this.formData.championshipId) {
      return [];
    }

    return this.seasons
      .filter((season) => season.championshipId === this.formData.championshipId)
      .sort((left, right) => Number(right.isCurrent) - Number(left.isCurrent) || right.startYear - left.startYear);
  }

  public get selectedSeason(): Season | null {
    return this.availableSeasons.find((season) => season.id === this.formData.seasonId) ?? null;
  }

  public get selectedChampionship(): Championship | null {
    return this.championships.find((championship) => championship.id === this.formData.championshipId) ?? null;
  }

  public get availablePhases(): SeasonPhase[] {
    const season = this.selectedSeason;
    if (!season) {
      return [];
    }

    const collectLeaves = (phases: SeasonPhase[]): SeasonPhase[] => phases.flatMap((phase) => (
      phase.children.length > 0 ? collectLeaves(phase.children) : [phase]
    ));
    const phaseLeaves = collectLeaves(season.phases);
    if (this.isRplChampionship(this.selectedChampionship)) {
      return phaseLeaves.filter((phase) => phase.type === 'regular');
    }
    if (this.isKhlChampionship(this.selectedChampionship)) {
      return phaseLeaves.filter((phase) => phase.type === 'regular' || phase.type === 'playoff_stage');
    }

    return phaseLeaves;
  }

  public get usesSplitSeasonPhasePicker(): boolean {
    return this.isKhlChampionship(this.selectedChampionship);
  }

  public get regularSeasonPhase(): SeasonPhase | null {
    return this.availablePhases.find((phase) => phase.type === 'regular') ?? null;
  }

  public get playoffStagePhases(): SeasonPhase[] {
    return this.availablePhases.filter((phase) => phase.type === 'playoff_stage');
  }

  public get participantDrafts(): SaveEventParticipantData[] {
    return this.formData.participants ?? [];
  }

  public get participantShareTotal(): number {
    return this.participantDrafts.reduce((sum, participant) => sum + participant.profitShareBasisPoints, 0);
  }

  public get availableParticipantUsers(): DashboardUser[] {
    const selectedUserIds = new Set(this.participantDrafts.map((participant) => participant.userId));
    return this.users.filter((user) => user.isActive && !selectedUserIds.has(user.id));
  }

  public get canSaveEvent(): boolean {
    return !this.saving
      && this.availableTeams.length >= 2
      && Boolean(this.formData.startsAt)
      && Boolean(this.formData.homeTeamId)
      && Boolean(this.formData.awayTeamId)
      && this.formData.homeTeamId !== this.formData.awayTeamId
      && Boolean(this.formData.seasonId)
      && Boolean(this.formData.seasonPhaseId)
      && this.participantDrafts.length > 0
      && this.participantShareTotal === 10000;
  }

  public onChampionshipChange(): void {
    if (!this.availableTeams.some((team) => team.id === this.formData.homeTeamId)) {
      this.formData.homeTeamId = '';
    }
    if (!this.availableTeams.some((team) => team.id === this.formData.awayTeamId)) {
      this.formData.awayTeamId = '';
    }
    if (!this.availableSeasons.some((season) => season.id === this.formData.seasonId)) {
      this.selectDefaultSeason();
      return;
    }
    this.selectDefaultPhase();
  }

  public onHomeTeamChange(): void {
    if (this.formData.homeTeamId === this.formData.awayTeamId) {
      this.formData.awayTeamId = '';
    }
  }

  public onAwayTeamChange(): void {
    if (this.formData.awayTeamId === this.formData.homeTeamId) {
      this.formData.homeTeamId = '';
    }
  }

  public onSeasonChange(): void {
    this.selectDefaultPhase();
  }

  public onSeasonPhaseModeChange(): void {
    if (this.seasonPhaseMode === 'regular') {
      this.formData.seasonPhaseId = this.regularSeasonPhase?.id ?? '';
      return;
    }

    if (!this.playoffStagePhases.some((phase) => phase.id === this.formData.seasonPhaseId)) {
      this.formData.seasonPhaseId = this.playoffStagePhases[0]?.id ?? '';
    }
  }

  public onPlayoffStageChange(): void {
    this.seasonPhaseMode = 'playoff';
  }

  public getPhaseName(phase: SeasonPhase): string {
    if (phase.type === 'regular') {
      return 'Регулярный сезон';
    }
    if (phase.type === 'playoff_stage') {
      return phase.name;
    }

    return phase.name;
  }

  public addParticipant(): void {
    if (!this.selectedParticipantUserId) {
      return;
    }

    this.setParticipantsByUserIds([
      ...this.participantDrafts.map((participant) => participant.userId),
      this.selectedParticipantUserId,
    ]);
    this.selectedParticipantUserId = '';
  }

  public removeParticipant(userId: string): void {
    this.setParticipantsByUserIds(this.participantDrafts
      .map((participant) => participant.userId)
      .filter((participantUserId) => participantUserId !== userId));
  }

  public getParticipantName(userId: string): string {
    const user = this.users.find((item) => item.id === userId);
    return user?.displayName || user?.username || 'Пользователь';
  }

  public formatShare(profitShareBasisPoints: number): string {
    const percent = profitShareBasisPoints / 100;
    return Number.isInteger(percent) ? percent.toString() : percent.toFixed(2);
  }

  private async loadDictionaries(): Promise<void> {
    const [
      teamsResponse,
      championshipsResponse,
      seasonsResponse,
      usersResponse,
      currentUserResponse,
    ] = await Promise.all([
      this.teamApi.getTeams(),
      this.championshipApi.getChampionships(),
      this.seasonApi.getSeasons(),
      this.userApi.getDirectoryUsers(),
      this.userApi.getMe(),
    ]);
    const teams = this.unwrapResponse(teamsResponse);
    const championships = this.unwrapResponse(championshipsResponse);
    const seasons = this.unwrapResponse(seasonsResponse);
    const users = this.unwrapResponse(usersResponse);
    const currentUser = this.unwrapResponse(currentUserResponse);
    this.teams = teams ?? [];
    this.championships = championships ?? [];
    this.seasons = seasons ?? [];
    this.users = (users ?? []).filter((user) => user.isActive);
    this.currentUser = currentUser;
    if (this.mode === 'create') {
      this.setDefaultParticipants();
    }
  }

  private async loadEvent(eventId: string): Promise<void> {
    const response = await this.eventApi.getEvent(eventId);
    const event = this.unwrapResponse(response);
    if (!event) {
      this.errorMessage = 'Событие не найдено.';
      return;
    }

    this.formData = this.mapEventToForm(event);
    this.syncSeasonPhaseMode();
  }

  private unwrapResponse<ResponseType>(response: HttpResponse<ResponseType>): ResponseType | null {
    if (response.status === 'failed') {
      this.errorMessage = response.errorMessage;
      return null;
    }

    return response.response;
  }

  private mapEventToForm(event: SportsEvent): SaveEventData {
    return {
      startsAt: this.formatInputDate(event.startsAt),
      championshipId: event.championshipId ?? '',
      homeTeamId: event.homeTeamId,
      awayTeamId: event.awayTeamId,
      seasonId: event.seasonId ?? '',
      seasonPhaseId: event.seasonPhaseId ?? '',
      status: event.status,
      participants: event.participants.map((participant) => ({
        userId: participant.userId,
        profitShareBasisPoints: participant.profitShareBasisPoints,
      })),
    };
  }

  private createEmptyForm(): SaveEventData {
    return {
      startsAt: this.formatInputDate(new Date().toISOString()),
      championshipId: '',
      homeTeamId: '',
      awayTeamId: '',
      seasonId: '',
      seasonPhaseId: '',
      status: 'planned',
      participants: [],
    };
  }

  private formatInputDate(value: string): string {
    const date = new Date(value);
    const offset = date.getTimezoneOffset() * 60000;
    return new Date(date.getTime() - offset).toISOString().slice(0, 10);
  }

  private setDefaultParticipants(): void {
    if (this.participantDrafts.length > 0) {
      return;
    }

    const defaultUser = this.currentUser && this.users.some((user) => user.id === this.currentUser?.id)
      ? this.currentUser
      : this.findIva52User() ?? this.users[0] ?? null;
    if (defaultUser) {
      this.setParticipantsByUserIds([
        defaultUser.id,
      ]);
    }
  }

  private selectDefaultSeason(): void {
    const defaultSeason = this.availableSeasons.find((season) => season.isCurrent) ?? this.availableSeasons[0] ?? null;
    this.formData.seasonId = defaultSeason?.id ?? '';
    this.formData.seasonPhaseId = '';
    this.selectDefaultPhase();
  }

  private selectDefaultPhase(): void {
    if (this.availablePhases.some((phase) => phase.id === this.formData.seasonPhaseId)) {
      this.syncSeasonPhaseMode();
      return;
    }

    const regularPhase = this.regularSeasonPhase;
    const defaultPhase = regularPhase ?? this.availablePhases[0] ?? null;
    this.formData.seasonPhaseId = defaultPhase?.id ?? '';
    this.syncSeasonPhaseMode();
  }

  private syncSeasonPhaseMode(): void {
    const selectedPhase = this.availablePhases.find((phase) => phase.id === this.formData.seasonPhaseId);
    this.seasonPhaseMode = selectedPhase?.type === 'playoff_stage' ? 'playoff' : 'regular';
  }

  private isKhlChampionship(championship: Championship | null): boolean {
    return this.normalizeChampionshipName(championship).includes('khl') || this.normalizeChampionshipName(championship).includes('кхл');
  }

  private isRplChampionship(championship: Championship | null): boolean {
    const normalized = this.normalizeChampionshipName(championship);
    return normalized.includes('rpl') || normalized.includes('рпл');
  }

  private normalizeChampionshipName(championship: Championship | null): string {
    return `${championship?.slug ?? ''} ${championship?.name ?? ''}`.toLowerCase();
  }

  private setParticipantsByUserIds(userIds: string[]): void {
    const uniqueUserIds = [...new Set(userIds.filter((userId) => this.users.some((user) => user.id === userId)))];
    this.formData.participants = this.distributeParticipantShares(uniqueUserIds);
  }

  private distributeParticipantShares(userIds: string[]): SaveEventParticipantData[] {
    if (userIds.length === 0) {
      return [];
    }

    const baseShare = Math.floor(10000 / userIds.length);
    const remainder = 10000 - baseShare * userIds.length;
    const remainderUserId = this.getRemainderUserId(userIds);

    return userIds.map((userId) => ({
      userId,
      profitShareBasisPoints: baseShare + (userId === remainderUserId ? remainder : 0),
    }));
  }

  private getRemainderUserId(userIds: string[]): string {
    const iva52User = this.findIva52User();
    if (iva52User && userIds.includes(iva52User.id)) {
      return iva52User.id;
    }

    if (this.currentUser && userIds.includes(this.currentUser.id)) {
      return this.currentUser.id;
    }

    return userIds[0];
  }

  private findIva52User(): DashboardUser | null {
    return this.users.find((user) => [
      user.username,
      user.displayName ?? '',
    ].some((value) => this.normalizeUserHandle(value) === 'iva52')) ?? null;
  }

  private normalizeUserHandle(value: string): string {
    return value.toLowerCase().replace(/[^a-zа-я0-9]/g, '');
  }

  private handleUnknownError(error: unknown): void {
    this.errorMessage = error instanceof Error ? error.message : 'Не удалось выполнить действие.';
  }
}
